import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, createTextVNode as _createTextVNode, withCtx as _withCtx, openBlock as _openBlock, createBlock as _createBlock } from "vue"

import { onMounted } from 'vue'
import LegalPageLayout from '@/components/LegalPageLayout.vue'
import { seoManager } from '@/utils/seoUtils'

// Dynamic last updated date - in a real app, this could come from a config file or API
const lastUpdated = '2024-01-15'


export default /*@__PURE__*/_defineComponent({
  __name: 'PrivacyPolicy',
  setup(__props) {

onMounted(() => {
  // Set comprehensive SEO tags using the new SEO manager
  const metaTags = {
    title: 'Privacy Policy - WallpaperHub',
    description: 'Learn how WallpaperHub collects, uses, and protects your personal information. Our comprehensive privacy policy covers data collection, cookies, third-party services, and your rights under GDPR.',
    keywords: 'privacy policy, data protection, GDPR, cookies, personal information, wallpaper app, data collection, user rights, third-party services, AdSense privacy',
    robots: 'index, follow',
    ogType: 'website'
  };
  
  seoManager.applyMetaTags(metaTags);
})

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(LegalPageLayout, {
    title: "Privacy Policy",
    "last-updated": lastUpdated,
    "show-table-of-contents": true
  }, {
    default: _withCtx(() => _cache[0] || (_cache[0] = [
      _createElementVNode("div", { class: "privacy-policy-content" }, [
        _createElementVNode("section", {
          id: "introduction",
          role: "region",
          "aria-labelledby": "introduction-heading"
        }, [
          _createElementVNode("h2", { id: "introduction-heading" }, "Introduction"),
          _createElementVNode("p", null, " Welcome to WallpaperHub. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website and use our services. We are committed to protecting your privacy and ensuring transparency about our data practices. "),
          _createElementVNode("p", null, " By using our service, you agree to the collection and use of information in accordance with this policy. If you do not agree with our policies and practices, please do not use our service. ")
        ]),
        _createElementVNode("section", {
          id: "information-we-collect",
          role: "region",
          "aria-labelledby": "information-we-collect-heading"
        }, [
          _createElementVNode("h2", { id: "information-we-collect-heading" }, "Information We Collect"),
          _createElementVNode("h3", { id: "personal-information" }, "Personal Information"),
          _createElementVNode("p", null, "We may collect personal information that you voluntarily provide to us when you:"),
          _createElementVNode("ul", null, [
            _createElementVNode("li", null, "Create an account or register for our services"),
            _createElementVNode("li", null, "Upload wallpapers or other content"),
            _createElementVNode("li", null, "Contact us for support or inquiries"),
            _createElementVNode("li", null, "Subscribe to our newsletter or marketing communications"),
            _createElementVNode("li", null, "Participate in surveys or promotional activities")
          ]),
          _createElementVNode("p", null, "This information may include:"),
          _createElementVNode("ul", null, [
            _createElementVNode("li", null, "Name and email address"),
            _createElementVNode("li", null, "Profile information and preferences"),
            _createElementVNode("li", null, "Content you upload (wallpapers, comments, ratings)"),
            _createElementVNode("li", null, "Communication preferences")
          ]),
          _createElementVNode("h3", { id: "automatically-collected-information" }, "Automatically Collected Information"),
          _createElementVNode("p", null, "When you visit our website, we automatically collect certain information about your device and usage patterns:"),
          _createElementVNode("ul", null, [
            _createElementVNode("li", null, "IP address and general location information"),
            _createElementVNode("li", null, "Browser type and version"),
            _createElementVNode("li", null, "Operating system and device information"),
            _createElementVNode("li", null, "Pages visited and time spent on our site"),
            _createElementVNode("li", null, "Referring website and search terms used"),
            _createElementVNode("li", null, "Download history and preferences")
          ])
        ]),
        _createElementVNode("section", {
          id: "how-we-use-information",
          role: "region",
          "aria-labelledby": "how-we-use-information-heading"
        }, [
          _createElementVNode("h2", { id: "how-we-use-information-heading" }, "How We Use Your Information"),
          _createElementVNode("p", null, "We use the information we collect for various purposes, including:"),
          _createElementVNode("h3", { id: "service-provision" }, "Service Provision"),
          _createElementVNode("ul", null, [
            _createElementVNode("li", null, "Providing and maintaining our wallpaper service"),
            _createElementVNode("li", null, "Processing your requests and transactions"),
            _createElementVNode("li", null, "Personalizing your experience and content recommendations"),
            _createElementVNode("li", null, "Managing your account and preferences")
          ]),
          _createElementVNode("h3", { id: "communication" }, "Communication"),
          _createElementVNode("ul", null, [
            _createElementVNode("li", null, "Responding to your inquiries and support requests"),
            _createElementVNode("li", null, "Sending important service updates and notifications"),
            _createElementVNode("li", null, "Providing marketing communications (with your consent)"),
            _createElementVNode("li", null, "Conducting surveys and gathering feedback")
          ]),
          _createElementVNode("h3", { id: "improvement-and-analytics" }, "Service Improvement and Analytics"),
          _createElementVNode("ul", null, [
            _createElementVNode("li", null, "Analyzing usage patterns to improve our services"),
            _createElementVNode("li", null, "Monitoring and preventing fraud and abuse"),
            _createElementVNode("li", null, "Ensuring technical functionality and security"),
            _createElementVNode("li", null, "Developing new features and services")
          ])
        ]),
        _createElementVNode("section", {
          id: "information-sharing",
          role: "region",
          "aria-labelledby": "information-sharing-heading"
        }, [
          _createElementVNode("h2", { id: "information-sharing-heading" }, "Information Sharing and Disclosure"),
          _createElementVNode("p", null, "We do not sell, trade, or otherwise transfer your personal information to third parties, except in the following circumstances:"),
          _createElementVNode("h3", { id: "service-providers" }, "Service Providers"),
          _createElementVNode("p", null, "We may share your information with trusted third-party service providers who assist us in operating our website and providing our services:"),
          _createElementVNode("ul", null, [
            _createElementVNode("li", null, [
              _createElementVNode("strong", null, "Firebase (Google):"),
              _createTextVNode(" For authentication, database, and hosting services")
            ]),
            _createElementVNode("li", null, [
              _createElementVNode("strong", null, "Cloudinary:"),
              _createTextVNode(" For image storage, processing, and delivery")
            ]),
            _createElementVNode("li", null, [
              _createElementVNode("strong", null, "Google AdSense:"),
              _createTextVNode(" For advertising services (see Advertising section below)")
            ])
          ]),
          _createElementVNode("h3", { id: "legal-requirements" }, "Legal Requirements"),
          _createElementVNode("p", null, "We may disclose your information if required to do so by law or in response to:"),
          _createElementVNode("ul", null, [
            _createElementVNode("li", null, "Valid legal processes (subpoenas, court orders)"),
            _createElementVNode("li", null, "Government requests or investigations"),
            _createElementVNode("li", null, "Protecting our rights, property, or safety"),
            _createElementVNode("li", null, "Preventing fraud or illegal activities")
          ]),
          _createElementVNode("h3", { id: "business-transfers" }, "Business Transfers"),
          _createElementVNode("p", null, "In the event of a merger, acquisition, or sale of assets, your information may be transferred as part of the business transaction. We will notify you of any such change in ownership or control.")
        ]),
        _createElementVNode("section", {
          id: "data-storage-security",
          role: "region",
          "aria-labelledby": "data-storage-security-heading"
        }, [
          _createElementVNode("h2", { id: "data-storage-security-heading" }, "Data Storage and Security"),
          _createElementVNode("h3", { id: "data-security" }, "Data Security"),
          _createElementVNode("p", null, "We implement appropriate technical and organizational security measures to protect your personal information against unauthorized access, alteration, disclosure, or destruction. These measures include:"),
          _createElementVNode("ul", null, [
            _createElementVNode("li", null, "Encryption of data in transit and at rest"),
            _createElementVNode("li", null, "Regular security assessments and updates"),
            _createElementVNode("li", null, "Access controls and authentication mechanisms"),
            _createElementVNode("li", null, "Secure hosting infrastructure")
          ]),
          _createElementVNode("h3", { id: "data-retention" }, "Data Retention"),
          _createElementVNode("p", null, "We retain your personal information only for as long as necessary to fulfill the purposes outlined in this Privacy Policy, unless a longer retention period is required or permitted by law. When we no longer need your information, we will securely delete or anonymize it."),
          _createElementVNode("h3", { id: "international-transfers" }, "International Data Transfers"),
          _createElementVNode("p", null, "Your information may be transferred to and processed in countries other than your own. We ensure that such transfers comply with applicable data protection laws and implement appropriate safeguards.")
        ]),
        _createElementVNode("section", {
          id: "cookies-tracking",
          role: "region",
          "aria-labelledby": "cookies-tracking-heading"
        }, [
          _createElementVNode("h2", { id: "cookies-tracking-heading" }, "Cookies and Tracking Technologies"),
          _createElementVNode("h3", { id: "what-are-cookies" }, "What Are Cookies"),
          _createElementVNode("p", null, "Cookies are small text files stored on your device when you visit our website. We use cookies and similar tracking technologies to enhance your browsing experience and analyze site usage."),
          _createElementVNode("h3", { id: "types-of-cookies" }, "Types of Cookies We Use"),
          _createElementVNode("ul", null, [
            _createElementVNode("li", null, [
              _createElementVNode("strong", null, "Essential Cookies:"),
              _createTextVNode(" Necessary for basic website functionality and security")
            ]),
            _createElementVNode("li", null, [
              _createElementVNode("strong", null, "Analytics Cookies:"),
              _createTextVNode(" Help us understand how visitors interact with our website")
            ]),
            _createElementVNode("li", null, [
              _createElementVNode("strong", null, "Preference Cookies:"),
              _createTextVNode(" Remember your settings and preferences")
            ]),
            _createElementVNode("li", null, [
              _createElementVNode("strong", null, "Advertising Cookies:"),
              _createTextVNode(" Used to deliver relevant advertisements (see below)")
            ])
          ]),
          _createElementVNode("h3", { id: "managing-cookies" }, "Managing Cookies"),
          _createElementVNode("p", null, "You can control and manage cookies through your browser settings. However, disabling certain cookies may affect the functionality of our website. Most browsers allow you to:"),
          _createElementVNode("ul", null, [
            _createElementVNode("li", null, "View and delete cookies"),
            _createElementVNode("li", null, "Block cookies from specific sites"),
            _createElementVNode("li", null, "Block third-party cookies"),
            _createElementVNode("li", null, "Clear all cookies when you close your browser")
          ])
        ]),
        _createElementVNode("section", {
          id: "third-party-services",
          role: "region",
          "aria-labelledby": "third-party-services-heading"
        }, [
          _createElementVNode("h2", { id: "third-party-services-heading" }, "Third-Party Services"),
          _createElementVNode("h3", { id: "google-adsense" }, "Google AdSense"),
          _createElementVNode("p", null, "We use Google AdSense to display advertisements on our website. Google AdSense may use cookies and other tracking technologies to:"),
          _createElementVNode("ul", null, [
            _createElementVNode("li", null, "Display personalized advertisements based on your interests"),
            _createElementVNode("li", null, "Measure ad performance and effectiveness"),
            _createElementVNode("li", null, "Prevent fraud and abuse")
          ]),
          _createElementVNode("p", null, [
            _createTextVNode("You can opt out of personalized advertising by visiting "),
            _createElementVNode("a", {
              href: "https://www.google.com/settings/ads",
              target: "_blank",
              rel: "noopener noreferrer"
            }, "Google's Ad Settings"),
            _createTextVNode(" or through the "),
            _createElementVNode("a", {
              href: "http://www.aboutads.info/choices/",
              target: "_blank",
              rel: "noopener noreferrer"
            }, "Digital Advertising Alliance's opt-out page"),
            _createTextVNode(".")
          ]),
          _createElementVNode("h3", { id: "firebase-services" }, "Firebase Services"),
          _createElementVNode("p", null, "We use Google Firebase for various backend services including:"),
          _createElementVNode("ul", null, [
            _createElementVNode("li", null, [
              _createElementVNode("strong", null, "Authentication:"),
              _createTextVNode(" Managing user accounts and login")
            ]),
            _createElementVNode("li", null, [
              _createElementVNode("strong", null, "Firestore Database:"),
              _createTextVNode(" Storing application data")
            ]),
            _createElementVNode("li", null, [
              _createElementVNode("strong", null, "Cloud Storage:"),
              _createTextVNode(" Storing user-uploaded content")
            ]),
            _createElementVNode("li", null, [
              _createElementVNode("strong", null, "Analytics:"),
              _createTextVNode(" Understanding app usage and performance")
            ])
          ]),
          _createElementVNode("p", null, "Firebase's data processing is governed by Google's Privacy Policy and Firebase's specific privacy practices."),
          _createElementVNode("h3", { id: "cloudinary" }, "Cloudinary"),
          _createElementVNode("p", null, "We use Cloudinary for image storage, processing, and delivery. Cloudinary may process and store wallpaper images you upload or view. Their data processing is governed by Cloudinary's Privacy Policy.")
        ]),
        _createElementVNode("section", {
          id: "user-rights",
          role: "region",
          "aria-labelledby": "user-rights-heading"
        }, [
          _createElementVNode("h2", { id: "user-rights-heading" }, "Your Rights and Choices"),
          _createElementVNode("h3", { id: "account-information" }, "Account Information"),
          _createElementVNode("p", null, "If you have an account with us, you can:"),
          _createElementVNode("ul", null, [
            _createElementVNode("li", null, "Access and update your profile information"),
            _createElementVNode("li", null, "Change your communication preferences"),
            _createElementVNode("li", null, "Delete content you have uploaded"),
            _createElementVNode("li", null, "Deactivate your account")
          ]),
          _createElementVNode("h3", { id: "data-rights" }, "Data Rights"),
          _createElementVNode("p", null, "Depending on your location, you may have the following rights regarding your personal information:"),
          _createElementVNode("ul", null, [
            _createElementVNode("li", null, [
              _createElementVNode("strong", null, "Access:"),
              _createTextVNode(" Request a copy of the personal information we hold about you")
            ]),
            _createElementVNode("li", null, [
              _createElementVNode("strong", null, "Correction:"),
              _createTextVNode(" Request correction of inaccurate or incomplete information")
            ]),
            _createElementVNode("li", null, [
              _createElementVNode("strong", null, "Deletion:"),
              _createTextVNode(" Request deletion of your personal information")
            ]),
            _createElementVNode("li", null, [
              _createElementVNode("strong", null, "Portability:"),
              _createTextVNode(" Request transfer of your data to another service")
            ]),
            _createElementVNode("li", null, [
              _createElementVNode("strong", null, "Restriction:"),
              _createTextVNode(" Request limitation of processing in certain circumstances")
            ]),
            _createElementVNode("li", null, [
              _createElementVNode("strong", null, "Objection:"),
              _createTextVNode(" Object to processing based on legitimate interests")
            ])
          ]),
          _createElementVNode("h3", { id: "exercising-rights" }, "Exercising Your Rights"),
          _createElementVNode("p", null, [
            _createTextVNode("To exercise any of these rights, please contact us at "),
            _createElementVNode("a", { href: "mailto:collinsodeny@gmail.com" }, "collinsodeny@gmail.com"),
            _createTextVNode(". We will respond to your request within the timeframe required by applicable law.")
          ])
        ]),
        _createElementVNode("section", {
          id: "gdpr-compliance",
          role: "region",
          "aria-labelledby": "gdpr-compliance-heading"
        }, [
          _createElementVNode("h2", { id: "gdpr-compliance-heading" }, "GDPR Compliance (EU Users)"),
          _createElementVNode("h3", { id: "legal-basis" }, "Legal Basis for Processing"),
          _createElementVNode("p", null, "For users in the European Union, we process your personal information based on the following legal grounds:"),
          _createElementVNode("ul", null, [
            _createElementVNode("li", null, [
              _createElementVNode("strong", null, "Consent:"),
              _createTextVNode(" When you have given clear consent for specific processing activities")
            ]),
            _createElementVNode("li", null, [
              _createElementVNode("strong", null, "Contract:"),
              _createTextVNode(" When processing is necessary to provide our services")
            ]),
            _createElementVNode("li", null, [
              _createElementVNode("strong", null, "Legal Obligation:"),
              _createTextVNode(" When we must process data to comply with legal requirements")
            ]),
            _createElementVNode("li", null, [
              _createElementVNode("strong", null, "Legitimate Interest:"),
              _createTextVNode(" When we have a legitimate business interest that doesn't override your rights")
            ])
          ]),
          _createElementVNode("h3", { id: "data-protection-rights" }, "Enhanced Data Protection Rights"),
          _createElementVNode("p", null, "As an EU resident, you have additional rights under the GDPR:"),
          _createElementVNode("ul", null, [
            _createElementVNode("li", null, "Right to withdraw consent at any time"),
            _createElementVNode("li", null, "Right to lodge a complaint with your local data protection authority"),
            _createElementVNode("li", null, "Right to receive information about data breaches that may affect you"),
            _createElementVNode("li", null, "Right to data protection by design and by default")
          ]),
          _createElementVNode("h3", { id: "data-transfers" }, "Data Transfers Outside the EU"),
          _createElementVNode("p", null, "When we transfer your data outside the EU, we ensure adequate protection through:"),
          _createElementVNode("ul", null, [
            _createElementVNode("li", null, "Adequacy decisions by the European Commission"),
            _createElementVNode("li", null, "Standard Contractual Clauses (SCCs)"),
            _createElementVNode("li", null, "Binding Corporate Rules"),
            _createElementVNode("li", null, "Certification schemes and codes of conduct")
          ])
        ]),
        _createElementVNode("section", {
          id: "childrens-privacy",
          role: "region",
          "aria-labelledby": "childrens-privacy-heading"
        }, [
          _createElementVNode("h2", { id: "childrens-privacy-heading" }, "Children's Privacy"),
          _createElementVNode("p", null, "Our service is not intended for children under the age of 13. We do not knowingly collect personal information from children under 13. If you are a parent or guardian and believe your child has provided us with personal information, please contact us immediately."),
          _createElementVNode("p", null, "If we discover that we have collected personal information from a child under 13 without parental consent, we will take steps to remove that information from our servers promptly."),
          _createElementVNode("p", null, "For users between 13 and 18, we recommend parental guidance when using our services and sharing personal information online.")
        ]),
        _createElementVNode("section", {
          id: "changes-to-policy",
          role: "region",
          "aria-labelledby": "changes-to-policy-heading"
        }, [
          _createElementVNode("h2", { id: "changes-to-policy-heading" }, "Changes to This Privacy Policy"),
          _createElementVNode("p", null, "We may update this Privacy Policy from time to time to reflect changes in our practices, technology, legal requirements, or other factors. When we make changes, we will:"),
          _createElementVNode("ul", null, [
            _createElementVNode("li", null, "Update the \"Last Updated\" date at the top of this policy"),
            _createElementVNode("li", null, "Notify you of significant changes through our website or email"),
            _createElementVNode("li", null, "Provide a summary of key changes when appropriate"),
            _createElementVNode("li", null, "Give you the opportunity to review the updated policy")
          ]),
          _createElementVNode("p", null, "We encourage you to review this Privacy Policy periodically to stay informed about how we protect your information.")
        ]),
        _createElementVNode("section", {
          id: "contact-information",
          role: "region",
          "aria-labelledby": "contact-information-heading"
        }, [
          _createElementVNode("h2", { id: "contact-information-heading" }, "Contact Information"),
          _createElementVNode("p", null, "If you have any questions, concerns, or requests regarding this Privacy Policy or our data practices, please contact us:"),
          _createElementVNode("div", { class: "contact-details" }, [
            _createElementVNode("p", null, [
              _createElementVNode("strong", null, "Email:"),
              _createTextVNode(),
              _createElementVNode("a", { href: "mailto:privacy@wallpaperhub.com" }, "privacy@wallpaperhub.com")
            ]),
            _createElementVNode("p", null, [
              _createElementVNode("strong", null, "General Support:"),
              _createTextVNode(),
              _createElementVNode("a", { href: "mailto:support@wallpaperhub.com" }, "support@wallpaperhub.com")
            ]),
            _createElementVNode("p", null, [
              _createElementVNode("strong", null, "Data Protection Officer:"),
              _createTextVNode(),
              _createElementVNode("a", { href: "mailto:dpo@wallpaperhub.com" }, "dpo@wallpaperhub.com")
            ])
          ]),
          _createElementVNode("p", null, "We will make every effort to respond to your inquiries promptly and address any concerns you may have about your privacy and data protection.")
        ])
      ], -1)
    ])),
    _: 1,
    __: [0]
  }))
}
}

})